import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * When the browser fires `online`, refetch all queries that currently have observers
 * (fixes empty/error UI after connectivity returns).
 *
 * When the tab becomes visible again, refetch only queries in error state — covers cases
 * where `fetch` failed with "Failed to fetch" but `navigator.onLine` never flipped false,
 * so no `online` event fires when the network is actually back.
 */
export function useQueryRefetchOnNetworkRecovery(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const qc = queryClient;

    const onOnline = () => {
      void qc.invalidateQueries({ refetchType: "active" });
    };

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void qc.invalidateQueries({
        refetchType: "active",
        predicate: (q) => q.state.status === "error",
      });
    };

    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [queryClient]);
}
